import 'rsuite/dist/rsuite.min.css';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Tabs,
  Tab,
  Select,
  SelectItem,
  Divider,
} from '@nextui-org/react';
import { DateRangePicker } from 'rsuite';
import { Selection } from '@react-types/shared';
import ModalSlideFilter from '../../components/filters/modal-sidebar-filter';
import RetailEvolutionChart, {
  RetailGroupBy,
  RetailDisplayMode,
} from '../../components/charts/RetailEvolutionChart';
import { listSkusBySpecies } from '../../api/retail_skus';
import {
  getCountryLabel,
  getRegionForCountry,
  getRegionLabel,
  REGION_ORDER,
  type RetailRegion,
} from '../../utils/retail-labels';
import { RetailTargetSku } from '../../utils/types';
import type { RetailProperty, RetailPropertyAssignments } from '../../utils/retail-properties';
import useDataStore from '../../stores/useDataStore';
import { useTranslation } from '../../hooks/useTranslation';
import {
  EmptyState,
  FiltersToggle,
  PageHeader,
  PageLayout,
} from '../../components/ui';

interface MarketRetailProps {
  /** Forces a species_id (e.g., importers app forces cherry=2). Defaults to selectedSpecies from store. */
  forceSpeciesId?: number;
  actionsSlot?: React.ReactNode;
  targetSkuIds?: number[];
  properties?: RetailProperty[];
  propertyAssignments?: RetailPropertyAssignments;
}

const MarketRetail: React.FC<MarketRetailProps> = ({
  forceSpeciesId,
  actionsSlot,
  targetSkuIds,
  properties,
  propertyAssignments,
}) => {
  const { t } = useTranslation('market');
  const { selectedSpecies } = useDataStore();
  const effectiveSpeciesId = forceSpeciesId ?? selectedSpecies?.id;

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [retailGroupBy, setRetailGroupBy] = useState<RetailGroupBy>('region');
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const selectedProperty = properties?.find((property) => property.id === propertyId) ?? properties?.[0];
  const [retailDisplayMode, setRetailDisplayMode] =
    useState<RetailDisplayMode>('separado');
  const [retailPriceMode, setRetailPriceMode] = useState<'usd' | 'local'>(
    'usd'
  );
  const [retailCountries, setRetailCountries] = useState<string[]>([]);
  const [retailRetailers, setRetailRetailers] = useState<string[]>([]);
  const [retailGroups, setRetailGroups] = useState<string[]>([]);
  const [retailSkusForFilters, setRetailSkusForFilters] = useState<
    RetailTargetSku[]
  >([]);
  const [loadedFiltersSpeciesId, setLoadedFiltersSpeciesId] = useState<
    number | null
  >(null);
  const filtersLoading =
    effectiveSpeciesId != null && loadedFiltersSpeciesId !== effectiveSpeciesId;

  const groupTitle = {
    region: t('Por región'),
    group: t('Por grupo'),
    country: t('Por país'),
    retailer: t('Por retailer'),
    sku: t('Por SKU'),
    property: selectedProperty ? `${t('Por propiedades')} · ${selectedProperty.name}` : t('Por propiedades'),
  }[retailGroupBy];

  // Default date range: last 30 days (retail data accumulates from cron forward)
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    return [from, to];
  });

  // Fetch active SKUs to populate filter options
  useEffect(() => {
    if (!effectiveSpeciesId) {
      setRetailSkusForFilters([]);
      setLoadedFiltersSpeciesId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const skus = await listSkusBySpecies(effectiveSpeciesId);
        if (!cancelled) {
          setRetailSkusForFilters(skus.filter((s) => s.active));
          setLoadedFiltersSpeciesId(effectiveSpeciesId);
        }
      } catch (e) {
        console.error('Error loading retail SKUs for filters', e);
        if (!cancelled) {
          setRetailSkusForFilters([]);
          setLoadedFiltersSpeciesId(effectiveSpeciesId);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveSpeciesId]);

  // El grupo vive en el SKU (retail_prices no lo denormaliza): se resuelve
  // acá, con los SKUs que ya cargamos para los filtros, así siempre refleja
  // la asignación actual de superadmin.
  const skuGroups = useMemo(
    () =>
      new Map<number, string | null>(
        retailSkusForFilters.map((s) => [s.id, s.species_group ?? null])
      ),
    [retailSkusForFilters]
  );

  // Con SKUs seguidos, los filtros, los grupos y la moneda local tienen que
  // mirar solo esos: si no, ofrecen países, grupos o monedas que el gráfico
  // ya no muestra.
  const retailSkusInScope = useMemo(() => {
    if (!targetSkuIds?.length) return retailSkusForFilters;
    const followed = new Set(targetSkuIds);
    return retailSkusForFilters.filter((s) => followed.has(s.id));
  }, [retailSkusForFilters, targetSkuIds]);

  const groupOptions = useMemo(
    () =>
      Array.from(
        new Set(
          retailSkusInScope
            .map((s) => s.species_group)
            .filter((g): g is string => Boolean(g))
        )
      ).sort(),
    [retailSkusInScope]
  );

  // La agrupación por grupo solo tiene sentido si algún SKU tiene uno
  // asignado (definirlos en la especie no basta).
  const hasGroups = groupOptions.length > 0;

  // Si la especie cambia y la nueva no tiene grupos asignados, la vista
  // "Por grupo" deja de existir: volver a la vista por defecto.
  useEffect(() => {
    if (!filtersLoading && retailGroupBy === 'group' && !hasGroups) {
      setRetailGroupBy('region');
    }
  }, [filtersLoading, hasGroups, retailGroupBy]);


  const retailFilterOptions = useMemo(() => {
    const c = new Set<string>();
    const r = new Set<string>();
    retailSkusInScope.forEach((s) => {
      c.add(s.country);
      r.add(s.retailer);
    });
    return {
      countries: Array.from(c).sort(),
      retailers: Array.from(r).sort(),
    };
  }, [retailSkusInScope]);

  // Región = atajo para seleccionar países. La fuente de verdad SIEMPRE es
  // retailCountries: una región se muestra marcada si tiene al menos un país
  // seleccionado; togglearla agrega o quita todos sus países disponibles.
  const regionCountries = useMemo(() => {
    const map = new Map<RetailRegion, string[]>();
    retailFilterOptions.countries.forEach((c) => {
      const r = getRegionForCountry(c);
      map.set(r, [...(map.get(r) ?? []), c]);
    });
    return map;
  }, [retailFilterOptions.countries]);

  const availableRegions = useMemo(
    () => REGION_ORDER.filter((r) => regionCountries.has(r)),
    [regionCountries]
  );

  const selectedRegionKeys = useMemo(() => {
    const sel = new Set(retailCountries);
    return new Set(
      availableRegions.filter((r) =>
        regionCountries.get(r)!.some((c) => sel.has(c))
      )
    );
  }, [availableRegions, regionCountries, retailCountries]);

  const handleRegionSelection = (keys: Selection) => {
    const next = new Set(Array.from(keys) as RetailRegion[]);
    const countries = new Set(retailCountries);
    availableRegions.forEach((r) => {
      const was = selectedRegionKeys.has(r);
      const is = next.has(r);
      if (!was && is) regionCountries.get(r)!.forEach((c) => countries.add(c));
      if (was && !is) regionCountries.get(r)!.forEach((c) => countries.delete(c));
    });
    setRetailCountries(Array.from(countries));
  };

  // Local-currency view is only offered when the current filter selection
  // resolves to a single currency (one country, or several that share it —
  // e.g. the whole Eurozone in EUR).
  const localCurrency = useMemo(() => {
    const set = new Set<string>();
    retailSkusInScope.forEach((s) => {
      if (retailCountries.length && !retailCountries.includes(s.country)) return;
      if (retailRetailers.length && !retailRetailers.includes(s.retailer))
        return;
      if (s.currency) set.add(s.currency);
    });
    return set.size === 1 ? Array.from(set)[0] : null;
  }, [retailSkusInScope, retailCountries, retailRetailers]);
  const canRetailLocal = localCurrency != null;
  const effectivePriceMode =
    retailPriceMode === 'local' && canRetailLocal ? 'local' : 'usd';

  // Switching to local needs a single currency. If the current selection has
  // more than one, narrow it down to a single country first (the user can
  // then pick a different one) so the local price is unambiguous.
  const handleLocalMode = () => {
    if (!canRetailLocal) {
      const pool = retailCountries.length
        ? retailCountries
        : retailFilterOptions.countries;
      if (pool[0]) setRetailCountries([pool[0]]);
    }
    setRetailPriceMode('local');
  };

  // "Seleccionar/Deseleccionar todos" via a special `all` option, matching
  // the pattern used in MarketToday's exporter selector.
  const handleCountrySelection = (keys: Selection) => {
    const arr = Array.from(keys) as string[];
    if (arr.includes('all')) {
      setRetailCountries(
        retailCountries.length > 0 ? [] : [...retailFilterOptions.countries]
      );
    } else {
      setRetailCountries(arr);
    }
  };

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <ModalSlideFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      >
        <div className='bg-background p-4 pt-8 h-full overflow-y-auto'>
          <div className='space-y-4'>
            <p className='text-sm text-default-500 mb-1'>
              {t('Rango de Fechas')}
            </p>
            <div className='relative z-[100]'>
              <DateRangePicker
                value={dateRange}
                onChange={(val) =>
                  setDateRange(val as [Date, Date] | null)
                }
                character=' - '
                format='dd-MM-yyyy'
                className='w-full'
                isoWeek
                menuStyle={{ zIndex: 9999 }}
                placeholder={t('Seleccionar rango de fechas')}
              />
            </div>

            <Divider />
            <p className='text-sm text-default-500 mb-1'>{t('Vista')}</p>
            <Tabs
              fullWidth
              size='sm'
              aria-label={t('Vista')}
              selectedKey={retailDisplayMode}
              onSelectionChange={(k) =>
                setRetailDisplayMode(k as RetailDisplayMode)
              }
            >
              <Tab key='separado' title={t('Separado')} />
              <Tab key='promedio' title={t('Promedio')} />
            </Tabs>

            <Divider />
            <p className='text-sm text-default-500 mb-1'>{t('Moneda')}</p>
            <Tabs
              fullWidth
              size='sm'
              aria-label={t('Moneda')}
              selectedKey={effectivePriceMode}
              onSelectionChange={(k) =>
                k === 'local' ? handleLocalMode() : setRetailPriceMode('usd')
              }
            >
              <Tab key='usd' title='USD/kg' />
              <Tab
                key='local'
                title={canRetailLocal ? `${localCurrency}/kg` : t('Moneda local')}
              />
            </Tabs>
            {effectivePriceMode === 'local' && (
              <p className='text-xs text-default-400'>
                {t('La moneda local muestra un país a la vez.')}
              </p>
            )}
          </div>
        </div>
      </ModalSlideFilter>

      <PageLayout>
        <PageHeader
          title={t('Retail')}
          description={t(
            'Precios retail en góndola, normalizados a USD/kg y trackeados por SKU fijo.'
          )}
        />

        {/* Toolbar: filter toggle (matches MarketEvolution pattern) */}
        <div className='flex flex-wrap items-center gap-2 mb-4'>
          <FiltersToggle
            isOpen={isFilterOpen}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
            label={isFilterOpen ? t('Ocultar filtros') : t('Mostrar filtros')}
          />
          {actionsSlot}
        </div>
        {targetSkuIds?.length ? (
          <p className='text-sm text-default-500 mb-4'>
            {t('Mostrando precios de los SKUs que sigues.')} ({targetSkuIds.length})
          </p>
        ) : null}

        <div className='overflow-x-auto mb-4'>
          <div className='min-w-[480px]'>
            <Tabs
              aria-label={t('Agrupar por')}
              selectedKey={retailGroupBy}
              onSelectionChange={(key) =>
                setRetailGroupBy(key as RetailGroupBy)
              }
              size='lg'
              variant='underlined'
            >
              <Tab key='region' title={t('Por región')} />
              {hasGroups ? <Tab key='group' title={t('Por grupo')} /> : null}
              <Tab key='country' title={t('Por país')} />
              <Tab key='retailer' title={t('Por retailer')} />
              <Tab key='sku' title={t('Por SKU')} />
              {properties !== undefined ? <Tab key='property' title={t('Por propiedades')} /> : null}
            </Tabs>
          </div>
        </div>

        <Card className='p-6'>
          {retailGroupBy === 'property' && !selectedProperty ? (
            <EmptyState
              title={t('Configura una propiedad, como Calibre o Variedad, y asígnala a tus SKUs para compararlos aquí.')}
            />
          ) : effectiveSpeciesId != null ? (
            <>
              <RetailEvolutionChart
                speciesId={effectiveSpeciesId}
                targetSkuIds={targetSkuIds}
                dateRange={dateRange ?? undefined}
                countries={retailCountries}
                retailers={retailRetailers}
                groupBy={retailGroupBy}
                displayMode={retailDisplayMode}
                priceMode={effectivePriceMode}
                title={groupTitle}
                skuGroups={skuGroups}
                groups={retailGroups}
                property={selectedProperty}
                propertyAssignments={propertyAssignments}
                filtersLoading={filtersLoading}
                filtersSlot={
                  <>
                  {retailGroupBy === 'property' && selectedProperty && (
                    <div className='space-y-2 mb-4'>
                      <Select
                        label={t('Propiedad')}
                        size='sm'
                        className='w-full sm:max-w-xs'
                        disallowEmptySelection
                        selectedKeys={new Set([selectedProperty.id])}
                        onSelectionChange={(keys) => setPropertyId(String(Array.from(keys)[0]))}
                      >
                        {(properties ?? []).map((property) => (
                          <SelectItem key={property.id}>{property.name}</SelectItem>
                        ))}
                      </Select>
                      <p className='text-xs text-default-500'>
                        {t('Cada valor agrupa los SKUs que lo tienen asignado. Solo se incluyen SKUs con esta propiedad asignada.')}
                      </p>
                    </div>
                  )}
                  {/* Filtros del gráfico — misma grilla que EvolutionChartFilters */}
                  <div
                    className={`grid grid-cols-2 gap-4 mb-4 ${
                      hasGroups ? 'md:grid-cols-4' : 'md:grid-cols-3'
                    }`}
                  >
                    {hasGroups && (
                      <Select
                        label={t('Grupo')}
                        size='sm'
                        selectionMode='multiple'
                        placeholder={t('Todos')}
                        selectedKeys={new Set(retailGroups)}
                        onSelectionChange={(keys) =>
                          setRetailGroups(Array.from(keys) as string[])
                        }
                        className='min-w-40'
                      >
                        {groupOptions.map((g) => (
                          <SelectItem key={g} value={g}>
                            {g}
                          </SelectItem>
                        ))}
                      </Select>
                    )}
                    <Select
                      label={t('Región')}
                      size='sm'
                      selectionMode='multiple'
                      placeholder={t('Todas')}
                      selectedKeys={selectedRegionKeys}
                      onSelectionChange={handleRegionSelection}
                      isDisabled={availableRegions.length === 0}
                      className='min-w-40'
                    >
                      {availableRegions.map((r) => (
                        <SelectItem
                          key={r}
                          value={r}
                          textValue={getRegionLabel(t, r)}
                        >
                          {getRegionLabel(t, r)} ({regionCountries.get(r)!.length})
                        </SelectItem>
                      ))}
                    </Select>
                    <Select
                      label={t('País')}
                      size='sm'
                      selectionMode='multiple'
                      placeholder={t('Todos')}
                      selectedKeys={new Set(retailCountries)}
                      onSelectionChange={handleCountrySelection}
                      isDisabled={retailFilterOptions.countries.length === 0}
                      className='min-w-40'
                    >
                      {[
                        { key: 'all', label: retailCountries.length > 0 ? t('Deseleccionar todos') : t('Seleccionar todos') },
                        ...retailFilterOptions.countries.map((code) => ({ key: code, label: getCountryLabel(code) })),
                      ].map(({ key, label }) => (
                        <SelectItem key={key} value={key} textValue={label}>{label}</SelectItem>
                      ))}
                    </Select>
                    <Select
                      label={t('Retailer')}
                      size='sm'
                      selectionMode='multiple'
                      placeholder={t('Todos')}
                      selectedKeys={new Set(retailRetailers)}
                      onSelectionChange={(keys) =>
                        setRetailRetailers(Array.from(keys) as string[])
                      }
                      isDisabled={retailFilterOptions.retailers.length === 0}
                      className='min-w-40'
                    >
                      {retailFilterOptions.retailers.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                  </>
                }
              />
            </>
          ) : (
            <EmptyState title={t('Selecciona una especie para ver retail.')} />
          )}
        </Card>
      </PageLayout>
    </div>
  );
};

export default MarketRetail;
